import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const PRIMARY     = '#0d47a1';
const PRIMARY_MID = '#1565c0';

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const fmt2Date = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getDefaultFromDate = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
};

const fmtAmt = (n) => {
  const v = parseFloat(n) || 0;
  if (Math.abs(v) > 1e15) return '—';
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const buildGroups = (rows) => {
  const map = new Map();
  rows.forEach(r => {
    const key = r.BalanceSheetHeadId;
    if (!map.has(key)) map.set(key, { headName: r.BalanceSheetHeadName, items: [] });
    map.get(key).items.push(r);
  });
  return Array.from(map.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, v]) => v);
};

const BalanceSheet = () => {
  useSessionValidation();
  const { companyId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate]     = useState(fmt2Date(new Date()));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('0');

  useEffect(() => {
    if (!companyId || !token) return;
    fetchData('BranchApi', `/${companyId}`, token)
      .then(res => {
        if (res.responseCode === '1111') setBranches(res.response_data || []);
        else setBranches(res.response_data || res || []);
      })
      .catch(() => setBranches([]));
  }, [companyId, token]);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
        if (new Date(fromDate) > new Date(toDate)) {
            setData([]);
            setError('From Date cannot be greater than To Date');
            return;
        }
        fetchReport();
    }
  }, [companyId, token, fromDate, toDate, selectedBranch]); // eslint-disable-line

  const fetchReport = async () => {
    if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const branchParam = selectedBranch === '0' ? 0 : parseInt(selectedBranch);
      const res = await fetchData(
        'MISReportApi',
        `/BalanceSheetReport/${fromDate}/${toDate}/${companyId}/${branchParam}`,
        token
      );
      setData(res.response_data || res || []);
    } catch (e) {
      console.error(e);
      setError('Failed to load balance sheet data');
    } finally {
      setLoading(false);
    }
  };

  const hasTypeSplit = data.some(r => {
    const t = (r.BalanceSheetType || '').toLowerCase();
    return t === 'assets' || t === 'liabilities';
  });

  const assetData = hasTypeSplit
    ? data.filter(r => (r.BalanceSheetType || '').toLowerCase() === 'assets')
    : data.filter(r => {
        const h = (r.BalanceSheetHeadName || '').toLowerCase();
        return h.includes('debtor') || h.includes('bank') || h.includes('asset') || h.includes('income') || h.includes('stock') || h.includes('cash');
      });

  const liabData = hasTypeSplit
    ? data.filter(r => (r.BalanceSheetType || '').toLowerCase() === 'liabilities')
    : data.filter(r => {
        const h = (r.BalanceSheetHeadName || '').toLowerCase();
        return h.includes('creditor') || h.includes('capital') || h.includes('loan') || h.includes('expense') || h.includes('staff') || h.includes('occ');
      });

  const allGroups  = buildGroups(data);
  const assetGroups = buildGroups(assetData);
  const liabGroups  = buildGroups(liabData);
  const useSplit    = assetGroups.length > 0 && liabGroups.length > 0;

  const totalAssets = assetData.reduce((s, r) => {
    const v = parseFloat(r.ClosingBalance) || 0;
    return Math.abs(v) > 1e15 ? s : s + v;
  }, 0);

  const totalLiabilities = liabData.reduce((s, r) => {
    const v = parseFloat(r.ClosingBalance) || 0;
    return Math.abs(v) > 1e15 ? s : s + v;
  }, 0);

  const grandTotal = data.reduce((s, r) => {
    const v = parseFloat(r.ClosingBalance) || 0;
    return Math.abs(v) > 1e15 ? s : s + v;
  }, 0);

  const selectedBranchObj  = branches.find(b => String(b.Id) === String(selectedBranch));
  const selectedBranchName = selectedBranchObj ? (selectedBranchObj.Name || selectedBranchObj.BranchName || '') : '';

  /* ── Excel Export ── */
  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'BalanceSheet', 0);
    if (!data.length) return;

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Balance Sheet');

    const now = new Date();
    const genText = `Generated: ${now.toLocaleDateString('en-IN')}, ${now.toLocaleTimeString('en-IN')}  by ${username || ''}`;

    const thin = { style: 'thin' };
    const border = { top: thin, left: thin, bottom: thin, right: thin };

    const COL_WIDTHS = useSplit ? [36, 16, 2, 36, 16] : [40, 18];
    ws.columns = COL_WIDTHS.map(w => ({ width: w }));

    const TOTAL_COLS = useSplit ? 5 : 2;

    /* helper: merged header row */
    const addMergedRow = (text, font, fill, height = 18) => {
      const r = ws.addRow([text]);
      ws.mergeCells(r.number, 1, r.number, TOTAL_COLS);
      const cell = r.getCell(1);
      cell.value = text;
      cell.font = font;
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
      r.height = height;
      return r;
    };

    /* ── Title block ── */
    addMergedRow(companyName || '', { bold: true, size: 14, color: { argb: 'FF0d8392' } }, null);
    addMergedRow('BALANCE SHEET', { bold: true, size: 12, color: { argb: 'FF333333' } }, null);
    addMergedRow(`As on: ${toDate}`, { size: 10, color: { argb: 'FF666666' } }, null, 15);
    addMergedRow(genText, { size: 9, color: { argb: 'FF999999' } }, null, 14);
    ws.addRow([]); // spacer

    if (useSplit) {
      /* ════════════════════════════════════
         T-FORMAT  — cols: [L-name | L-amt | gap | A-name | A-amt]
         ════════════════════════════════════ */

      // Build flat row arrays for each side
      const liabFlat = [];
      liabGroups.forEach(g => {
        const ht = g.items.reduce((s, r) => { const v = parseFloat(r.ClosingBalance) || 0; return Math.abs(v) > 1e15 ? s : s + v; }, 0);
        liabFlat.push({ type: 'head', name: g.headName, amt: ht });
        g.items.forEach(r => {
          const v = parseFloat(r.ClosingBalance) || 0;
          liabFlat.push({ type: 'item', name: r.LedgerName, amt: Math.abs(v) > 1e15 ? null : v });
        });
      });
      liabFlat.push({ type: 'total', name: 'Total Liabilities', amt: totalLiabilities });

      const assetFlat = [];
      assetGroups.forEach(g => {
        const ht = g.items.reduce((s, r) => { const v = parseFloat(r.ClosingBalance) || 0; return Math.abs(v) > 1e15 ? s : s + v; }, 0);
        assetFlat.push({ type: 'head', name: g.headName, amt: ht });
        g.items.forEach(r => {
          const v = parseFloat(r.ClosingBalance) || 0;
          assetFlat.push({ type: 'item', name: r.LedgerName, amt: Math.abs(v) > 1e15 ? null : v });
        });
      });
      assetFlat.push({ type: 'total', name: 'Total Assets', amt: totalAssets });

      // Section title row: LIABILITIES | ASSETS
      const secRow = ws.addRow(['LIABILITIES', '', '', 'ASSETS', '']);
      secRow.height = 20;
      // Liabilities header
      ws.mergeCells(secRow.number, 1, secRow.number, 2);
      const lSec = secRow.getCell(1);
      lSec.value = 'LIABILITIES';
      lSec.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      lSec.alignment = { horizontal: 'center', vertical: 'middle' };
      lSec.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF880e4f' } };
      lSec.border = border;
      secRow.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF880e4f' } };
      secRow.getCell(2).border = border;
      // Gap col
      secRow.getCell(3).value = '';
      // Assets header
      ws.mergeCells(secRow.number, 4, secRow.number, 5);
      const aSec = secRow.getCell(4);
      aSec.value = 'ASSETS';
      aSec.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      aSec.alignment = { horizontal: 'center', vertical: 'middle' };
      aSec.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1b5e20' } };
      aSec.border = border;
      secRow.getCell(5).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1b5e20' } };
      secRow.getCell(5).border = border;

      // Column sub-headers: Particulars | Amount | | Particulars | Amount
      const subRow = ws.addRow(['Particulars', 'Amount', '', 'Particulars', 'Amount']);
      subRow.height = 16;
      [1, 2].forEach(c => {
        const cell = subRow.getCell(c);
        cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFad1457' } };
        cell.alignment = { horizontal: c === 2 ? 'right' : 'left', vertical: 'middle' };
        cell.border = border;
      });
      [4, 5].forEach(c => {
        const cell = subRow.getCell(c);
        cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2e7d32' } };
        cell.alignment = { horizontal: c === 5 ? 'right' : 'left', vertical: 'middle' };
        cell.border = border;
      });

      // Data rows
      const maxLen = Math.max(liabFlat.length, assetFlat.length);
      for (let i = 0; i < maxLen; i++) {
        const l = liabFlat[i];
        const a = assetFlat[i];
        const rowData = [
          l ? l.name : '', l ? (l.amt !== null && l.amt !== undefined ? l.amt : '') : '',
          '',
          a ? a.name : '', a ? (a.amt !== null && a.amt !== undefined ? a.amt : '') : '',
        ];
        const row = ws.addRow(rowData);
        row.height = 15;

        // Style Liabilities cells
        if (l) {
          const isLHead  = l.type === 'head';
          const isLTotal = l.type === 'total';
          const lBg = isLTotal ? 'FFfce4ec' : isLHead ? 'FFfce4ec' : (i % 2 === 0 ? 'FFFFFFFF' : 'FFfff0f4');

          const c1 = row.getCell(1);
          c1.font = { bold: isLHead || isLTotal, size: 10, color: { argb: isLHead || isLTotal ? 'FF880e4f' : 'FF374151' } };
          c1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: lBg } };
          c1.alignment = { horizontal: 'left', vertical: 'middle', indent: isLHead || isLTotal ? 0 : 2 };
          c1.border = border;

          const c2 = row.getCell(2);
          c2.font = { bold: isLHead || isLTotal, size: 10, color: { argb: isLHead || isLTotal ? 'FF880e4f' : 'FF374151' } };
          c2.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: lBg } };
          c2.alignment = { horizontal: 'right', vertical: 'middle' };
          c2.border = border;
          if (typeof rowData[1] === 'number') { c2.numFmt = '#,##0.00'; }
        }

        // Style Assets cells
        if (a) {
          const isAHead  = a.type === 'head';
          const isATotal = a.type === 'total';
          const aBg = isATotal ? 'FFe8f5e9' : isAHead ? 'FFe8f5e9' : (i % 2 === 0 ? 'FFFFFFFF' : 'FFf0fff4');

          const c4 = row.getCell(4);
          c4.font = { bold: isAHead || isATotal, size: 10, color: { argb: isAHead || isATotal ? 'FF1b5e20' : 'FF374151' } };
          c4.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: aBg } };
          c4.alignment = { horizontal: 'left', vertical: 'middle', indent: isAHead || isATotal ? 0 : 2 };
          c4.border = border;

          const c5 = row.getCell(5);
          c5.font = { bold: isAHead || isATotal, size: 10, color: { argb: isAHead || isATotal ? 'FF1b5e20' : 'FF374151' } };
          c5.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: aBg } };
          c5.alignment = { horizontal: 'right', vertical: 'middle' };
          c5.border = border;
          if (typeof rowData[4] === 'number') { c5.numFmt = '#,##0.00'; }
        }
      }

    } else {
      /* ════════════════════════════════════
         SINGLE grouped view
         ════════════════════════════════════ */
      const hRow = ws.addRow(['Particulars', 'Closing Balance']);
      hRow.height = 16;
      [1, 2].forEach(c => {
        const cell = hRow.getCell(c);
        cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1aabb9' } };
        cell.alignment = { horizontal: c === 2 ? 'right' : 'left', vertical: 'middle' };
        cell.border = border;
      });

      allGroups.forEach((g, gi) => {
        const ht = g.items.reduce((s, r) => { const v = parseFloat(r.ClosingBalance) || 0; return Math.abs(v) > 1e15 ? s : s + v; }, 0);
        const gRow = ws.addRow([g.headName, ht !== 0 ? ht : '']);
        gRow.height = 15;
        [1, 2].forEach(c => {
          const cell = gRow.getCell(c);
          cell.font = { bold: true, size: 10, color: { argb: 'FF0d8392' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe0f7fa' } };
          cell.alignment = { horizontal: c === 2 ? 'right' : 'left', vertical: 'middle' };
          cell.border = border;
          if (c === 2 && typeof ht === 'number') cell.numFmt = '#,##0.00';
        });

        g.items.forEach((r, ri) => {
          const v = parseFloat(r.ClosingBalance) || 0;
          const amt = Math.abs(v) > 1e15 ? '' : v;
          const iRow = ws.addRow([r.LedgerName, v !== 0 && Math.abs(v) <= 1e15 ? amt : '']);
          iRow.height = 14;
          const rowBg = ri % 2 === 0 ? 'FFFFFFFF' : 'FFe0f7fa';
          [1, 2].forEach(c => {
            const cell = iRow.getCell(c);
            cell.font = { size: 10, color: { argb: 'FF374151' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
            cell.alignment = { horizontal: c === 2 ? 'right' : 'left', vertical: 'middle', indent: c === 1 ? 2 : 0 };
            cell.border = border;
            if (c === 2 && typeof amt === 'number') cell.numFmt = '#,##0.00';
          });
        });
      });

      // Total Assets row
      const taRow = ws.addRow(['Total Assets', totalAssets]);
      taRow.height = 16;
      [1, 2].forEach(c => {
        const cell = taRow.getCell(c);
        cell.font = { bold: true, size: 10, color: { argb: 'FF1b5e20' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe8f5e9' } };
        cell.alignment = { horizontal: c === 2 ? 'right' : 'left', vertical: 'middle' };
        cell.border = border;
        if (c === 2) cell.numFmt = '#,##0.00';
      });

      // Total Liabilities row
      const tlRow = ws.addRow(['Total Liabilities', totalLiabilities]);
      tlRow.height = 16;
      [1, 2].forEach(c => {
        const cell = tlRow.getCell(c);
        cell.font = { bold: true, size: 10, color: { argb: 'FF880e4f' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFfce4ec' } };
        cell.alignment = { horizontal: c === 2 ? 'right' : 'left', vertical: 'middle' };
        cell.border = border;
        if (c === 2) cell.numFmt = '#,##0.00';
      });

      // Grand Total row
      const tRow = ws.addRow(['Grand Total', grandTotal]);
      tRow.height = 16;
      [1, 2].forEach(c => {
        const cell = tRow.getCell(c);
        cell.font = { bold: true, size: 10, color: { argb: 'FF0d8392' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFb2ebf2' } };
        cell.alignment = { horizontal: c === 2 ? 'right' : 'right', vertical: 'middle' };
        cell.border = border;
        if (c === 2) cell.numFmt = '#,##0.00';
      });
    }

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `BalanceSheet_${toDate}.xlsx`);
  };

  /* ── Styles ── */
  const thBase = {
    padding: '6px 10px', fontWeight: 700, fontSize: 11,
    whiteSpace: 'nowrap', border: `1px solid ${PRIMARY_MID}`,
    color: '#fff', background: PRIMARY_MID,
  };

  const renderGroups = (groups, headBg, headBorder, headColor, amtColor) =>
    groups.map((g, gi) => {
      const headTotal = g.items.reduce((s, r) => {
        const v = parseFloat(r.ClosingBalance) || 0;
        return Math.abs(v) > 1e15 ? s : s + v;
      }, 0);
      return (
        <tbody key={gi}>
          <tr style={{ background: headBg, borderBottom: `1px solid ${headBorder}` }}>
            <td style={{ padding: '6px 10px', fontSize: 12, fontWeight: 700, color: headColor }}>
              {g.headName}
            </td>
            <td style={{ padding: '6px 10px', fontSize: 12, fontWeight: 700, color: headColor, textAlign: 'right' }}>
              {headTotal !== 0 ? fmtAmt(headTotal) : ''}
            </td>
          </tr>
          {g.items.map((r, ri) => {
            const v = parseFloat(r.ClosingBalance) || 0;
            const display = Math.abs(v) > 1e15 ? '—' : (v !== 0 ? fmtAmt(v) : '');
            return (
              <tr key={ri} style={{ background: ri % 2 === 0 ? '#fff' : '#e0f7fa', borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '5px 10px 5px 22px', fontSize: 11.5, color: '#374151' }}>
                  {r.LedgerName}
                </td>
                <td style={{ padding: '5px 10px', fontSize: 11.5, color: amtColor, textAlign: 'right', fontWeight: 500 }}>
                  {display}
                </td>
              </tr>
            );
          })}
        </tbody>
      );
    });

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading balance sheet..." />

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #005662 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Balance Sheet
            <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>As on {toDate}</span>
          </span>

          <div style={{ flex: 1, minWidth: 4 }} />

          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />

          <select value={selectedBranch} onChange={e => setSelectedBranch(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 120 }}>
            <option value="0" style={{ background: '#005662', color: '#fff' }}>All Branches</option>
            {branches.map(b => (
              <option key={b.Id} value={String(b.Id)} style={{ background: '#005662', color: '#fff' }}>
                {b.Name || b.BranchName}
              </option>
            ))}
          </select>

          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!data.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Meta bar */}
        {data.length > 0 && (
          <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span>{fromDate} to {toDate}</span>
            {selectedBranch !== '0' && selectedBranchName && (
              <><span>|</span><span style={{ color: PRIMARY, fontWeight: 600 }}>{selectedBranchName}</span></>
            )}
            {useSplit && (
              <>
                <span>|</span>
                <span>Total Assets: <strong style={{ color: '#166534' }}>{fmtAmt(totalAssets)}</strong></span>
                <span>|</span>
                <span>Total Liabilities: <strong style={{ color: '#991b1b' }}>{fmtAmt(totalLiabilities)}</strong></span>
              </>
            )}
          </div>
        )}

        {/* Report body */}
        <div className="report-table-scroll" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 130px)', padding: 12 }}>

          {!loading && data.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>
              No data found for the selected period.
            </div>
          ) : useSplit ? (
            /* T-Format: Liabilities | Assets */
            <div style={{ display: 'flex', gap: 0, border: '1px solid #b2ebf2', borderRadius: 6, overflow: 'hidden' }}>

              {/* Liabilities */}
              <div style={{ flex: 1, borderRight: '3px solid #b2ebf2' }}>
                <div style={{ background: '#880e4f', padding: '7px 10px', textAlign: 'center', fontWeight: 700, fontSize: 12, color: '#fff' }}>
                  LIABILITIES
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thBase, background: '#ad1457', borderColor: '#e91e8c' }}>Particulars</th>
                      <th style={{ ...thBase, background: '#ad1457', borderColor: '#e91e8c', textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  {renderGroups(liabGroups, '#fce4ec', '#f48fb1', '#880e4f', '#880e4f')}
                  <tbody>
                    <tr style={{ background: '#fce4ec' }}>
                      <td style={{ padding: '7px 10px', fontWeight: 700, fontSize: 12, color: '#880e4f', textAlign: 'right', borderTop: '2px solid #f48fb1' }}>Total Liabilities</td>
                      <td style={{ padding: '7px 10px', fontWeight: 700, fontSize: 12, color: '#880e4f', textAlign: 'right', borderTop: '2px solid #f48fb1' }}>{fmtAmt(totalLiabilities)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Assets */}
              <div style={{ flex: 1 }}>
                <div style={{ background: '#1b5e20', padding: '7px 10px', textAlign: 'center', fontWeight: 700, fontSize: 12, color: '#fff' }}>
                  ASSETS
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thBase, background: '#2e7d32', borderColor: '#66bb6a' }}>Particulars</th>
                      <th style={{ ...thBase, background: '#2e7d32', borderColor: '#66bb6a', textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  {renderGroups(assetGroups, '#e8f5e9', '#a5d6a7', '#1b5e20', '#1b5e20')}
                  <tbody>
                    <tr style={{ background: '#e8f5e9' }}>
                      <td style={{ padding: '7px 10px', fontWeight: 700, fontSize: 12, color: '#1b5e20', textAlign: 'right', borderTop: '2px solid #a5d6a7' }}>Total Assets</td>
                      <td style={{ padding: '7px 10px', fontWeight: 700, fontSize: 12, color: '#1b5e20', textAlign: 'right', borderTop: '2px solid #a5d6a7' }}>{fmtAmt(totalAssets)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Single grouped view */
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #b2ebf2', borderRadius: 6 }}>
              <thead>
                <tr>
                  <th style={{ ...thBase, textAlign: 'left' }}>Particulars</th>
                  <th style={{ ...thBase, textAlign: 'right' }}>Closing Balance</th>
                </tr>
              </thead>
              {renderGroups(allGroups, '#e0f7fa', '#80cbc4', PRIMARY, PRIMARY)}
              <tbody>
                <tr style={{ background: '#b2ebf2', fontWeight: 700 }}>
                  <td style={{ padding: '7px 10px', fontWeight: 700, fontSize: 12, color: PRIMARY, textAlign: 'right', borderTop: `2px solid ${PRIMARY_MID}` }}>Grand Total</td>
                  <td style={{ padding: '7px 10px', fontWeight: 700, fontSize: 12, color: PRIMARY, textAlign: 'right', borderTop: `2px solid ${PRIMARY_MID}` }}>{fmtAmt(grandTotal)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </div>
  );
};

export default BalanceSheet;
